import React, { useState } from "react";
import "./StockQR.scss";

/**
 * Tap-to-select buttons (replaces dropdowns).
 * options: [{ value, label, sub?, disabled? }]
 * tone: "green" | "red" | "blue"   size: "md" | "sm"
 */
export const ChipGroup = ({
  options,
  value,
  onChange,
  tone = "blue",
  size = "md",
  emptyText = "Nothing to choose from.",
}) => {
  if (!options.length) return <p className="chip-empty">{emptyText}</p>;
  return (
    <div className="chip-group" role="radiogroup">
      {options.map((o) => {
        const selected =
          value !== "" && value != null && String(value) === String(o.value);
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={o.disabled}
            className={`chip chip--${tone} chip--${size} ${selected ? "active" : ""}`}
            onClick={() => onChange(o.value)}
          >
            <span>{o.label}</span>
            {o.sub && <small>{o.sub}</small>}
          </button>
        );
      })}
    </div>
  );
};

const naturalSort = (a, b) =>
  String(a).localeCompare(String(b), undefined, { numeric: true });

/**
 * Rack buttons for the chosen warehouse, plus "+ New rack" (the only place anything is typed).
 * Give it key={warehouseId} so it resets when the warehouse changes.
 */
export const RackPicker = ({
  racks = [],
  value,
  onChange,
  tone = "blue",
  size = "md",
}) => {
  const list = [...new Set(racks.filter(Boolean))].sort(naturalSort);
  // With no known racks the only option is to enter a new one
  const [custom, setCustom] = useState(
    () => list.length === 0 || (!!value && !list.includes(value)),
  );

  return (
    <div className="rack-picker">
      <div className="chip-group">
        {list.map((r) => (
          <button
            key={r}
            type="button"
            className={`chip chip--${tone} chip--${size} ${!custom && value === r ? "active" : ""}`}
            onClick={() => {
              setCustom(false);
              onChange(r);
            }}
          >
            📍 {r}
          </button>
        ))}
        <button
          type="button"
          className={`chip chip--new chip--${size} ${custom ? "active" : ""}`}
          onClick={() => {
            setCustom(true);
            onChange("");
          }}
        >
          + New rack
        </button>
        {custom && (
          <input
            className="rack-input"
            autoFocus={list.length > 0}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="New rack no."
          />
        )}
      </div>
    </div>
  );
};

/**
 * Warehouse buttons split in two groups so a NEW location is obvious:
 *  - "Has this item": warehouses that already hold stock of it
 *  - "New warehouse": every other warehouse (stock will be added there for the first time)
 * warehouses: [{ value, label }]   usedIds: warehouse ids that already hold this item
 */
export const WarehousePicker = ({
  warehouses,
  usedIds = [],
  value,
  onChange,
  tone = "blue",
  size = "md",
}) => {
  const used = new Set(usedIds.map(String));
  const inUse = warehouses.filter((w) => used.has(String(w.value)));
  const fresh = warehouses
    .filter((w) => !used.has(String(w.value)))
    .map((w) => ({ ...w, sub: inUse.length ? "New" : undefined }));

  if (!warehouses.length)
    return <p className="chip-empty">No active warehouses found.</p>;

  return (
    <div className="warehouse-picker">
      {inUse.length > 0 && (
        <>
          <span className="chip-label">Has this item</span>
          <ChipGroup
            options={inUse}
            value={value}
            onChange={onChange}
            tone={tone}
            size={size}
          />
        </>
      )}
      {fresh.length > 0 && (
        <>
          <span className="chip-label">
            {inUse.length ? "＋ New warehouse" : "Warehouses"}
          </span>
          <ChipGroup
            options={fresh}
            value={value}
            onChange={onChange}
            tone={tone}
            size={size}
          />
        </>
      )}
    </div>
  );
};

/**
 * One card per warehouse (name shown ONCE) with all its racks as buttons inside.
 * groups: [{ id, name, tag?, total?, racks: [{ key, rack?, label, sub? }] }]
 * Tapping a rack selects warehouse + rack in one go. allowNew adds "+ New rack" inside every warehouse card.
 */
export const WarehouseRackPicker = ({
  groups,
  selectedWarehouseId = "",
  selectedKey = "",
  customValue = "",
  onSelect,
  onNew,
  allowNew = false,
  tone = "neutral",
  emptyText = "Nothing to choose from.",
}) => {
  const [customWid, setCustomWid] = useState(null);
  if (!groups.length) return <p className="chip-empty">{emptyText}</p>;

  return (
    <div className="wr-picker">
      {groups.map((g) => {
        const wid = String(g.id);
        const warehouseSelected = String(selectedWarehouseId) === wid;
        const typing = allowNew && customWid === wid;
        return (
          <div
            key={wid}
            className={`wr-card ${warehouseSelected ? "selected" : ""}`}
          >
            <div className="wr-head">
              <strong>{g.name}</strong>
              {g.tag && <span className="wr-tag">{g.tag}</span>}
              {g.total && <span className="wr-total">{g.total}</span>}
            </div>
            <div className="chip-group">
              {g.racks.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  className={`chip chip--${tone} chip--md ${!typing && selectedKey === r.key ? "active" : ""}`}
                  onClick={() => {
                    setCustomWid(null);
                    onSelect(g, r);
                  }}
                >
                  <span>📍 {r.label}</span>
                  {r.sub && <small>{r.sub}</small>}
                </button>
              ))}
              {allowNew && (
                <button
                  type="button"
                  className={`chip chip--new chip--md ${typing ? "active" : ""}`}
                  onClick={() => {
                    setCustomWid(wid);
                    onNew(g, "");
                  }}
                >
                  + New rack
                </button>
              )}
              {typing && (
                <input
                  className="rack-input"
                  autoFocus
                  value={warehouseSelected ? customValue : ""}
                  onChange={(e) => onNew(g, e.target.value)}
                  placeholder="New rack no."
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
